import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {retailerT} from '@/lib/types/retailer.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderRetailerTableStats} from '@/lib/config/retailerTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useBrand} from '@/pages/(shared)/brands/detail/providers/brandDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

/**
 * Brand Retailer Detail Component
 */
export const BrandDetailRetailers = () => {
  const {isAdmin} = useAuth();
  const {brandId, searchRetailersToAdd, addRetailerToBrand, removeRetailerFromBrand} = useBrand();
  const {data: stats} = useViewQuery({view: 'retailer_stats'});
  const {toast} = useNotifications();

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<retailerT[]>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<retailerT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Retailer_Brand',
    title: 'Retailers Summary',
    columns: ['fk_brand_id', 'date_removed', ['Retailer', ['*']], ['Brand', ['*']]],
    searchColumn: 'Retailer.retailer_name',
    filters: [{column: 'fk_brand_id', operator: 'eq', value: brandId}],
    addButtonLabel: 'Add Retailer to Brand',
    noResultsMessage: 'No retailers associated with brand',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {
        header: 'Status',
        field: 'date_removed',
        render: row => (
          <span className={`badge bg-${!row.date_removed ? 'success' : 'danger'} text-white`}>
            {!row.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Retailer.retailer_name',
        render: row => (
          <Link
            to={`/retailers/${row.Retailer.retailer_id}`}
            className="text-primary hover:text-primary dark:text-primary"
          >
            {row.Retailer.retailer_name}
          </Link>
        ),
      },
      {
        header: 'Location',
        field: 'Retailer.retailer_city',
        render: row => `${row.Retailer.retailer_city}, ${row.Retailer.retailer_state}`,
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Retailer;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderRetailerTableStats(stats?.total, stats?.active, stats?.inactive);

  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchRetailersToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (retailer: retailerT) => {
    try {
      await addRetailerToBrand(retailer.retailer_id);
      toast.success({
        title: 'Retailer added to Brand',
        message: `Retailer ${retailer.retailer_name} added to Brand`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Retailer To Brand',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Retailer not found');

    try {
      await removeRetailerFromBrand(dataRowToDeleteRef.current.retailer_id);
      toast.success({
        title: 'Retailer Removed From Brand',
        message: `Retailer ${dataRowToDeleteRef.current.retailer_name} removed from Brand`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Retailer From Brand',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Retailers"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Retailer Name', field: 'retailer_name'}],
          valueField: 'retailer_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this retailer from the brand?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
